import { Box, Button, Divider, Group, Stack, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import Loading from "../../Loading";
import { MessageItemView } from "../../sharedItems/MessageItem";
import type { IFormValues } from "../AiPersonalityForm";
import AddItemButton from "./AddItemButton";
import ClearButton from "./ClearButton";
import CloneThreadButton from "./CloneThreadButton";
import DeleteThreadButton from "./DeleteThreadButton";
import EditLabelButton from "./EditLabelButton";
import EditReferenceButton from "./EditReferenceButton";
import Item from "./Item";
import itemClasses from "./Item.module.css";
import Judgement from "./Judgement";
import type { AiPersonalityQuery } from "./queries";
import classes from "./TuneThread.module.css";
import type { IThreadTestState } from "./useTest";

interface IProps {
    tuneThread: Exclude<AiPersonalityQuery["aiPersonality"], null>["tuneThreads"]["edges"][0]["node"];
    testState?: IThreadTestState;
    handleTest?: (values: IFormValues, threadId: number) => void;
    aiFormValues?: IFormValues;
    handleClear?: (threadId: number) => void;
}

function TuneThread({ tuneThread, testState, handleTest, aiFormValues, handleClear }: IProps) {
    const [threadOpened, { toggle: threadToggle }] = useDisclosure(false);
    const [refOpened, { toggle: refToggle }] = useDisclosure(false);

    return (
        <Stack>
            <Group justify="space-between">
                <Title order={5}>{tuneThread.label ?? "Untitled"}</Title>
                <EditLabelButton tuneThread={{ id: tuneThread.id, label: tuneThread.label }} />
            </Group>
            <Divider
                color="gray"
                label={`Thread ${threadOpened ? "(Hide)" : `(Show ${tuneThread.tuneThreadItems.edges.length})`}`}
                onClick={threadToggle}
                className={classes.clickDivider}
            />
            {threadOpened &&
                tuneThread.tuneThreadItems.edges.map(({ node }) => (
                    <Item item={node} key={node.id} tuneThread={tuneThread} />
                ))}
            <Divider color="gray" label="Result" />
            {testState ? (
                <>
                    {testState.status === "LOADING" && <Loading text="AI is thinking hard..." />}
                    {testState.status === "ERROR" && <Box>Error: {testState.error}</Box>}
                    {testState.status === "SUCCESS" && (
                        <>
                            {testState.items.map((item, index) => (
                                <Item key={index} item={{ ...item, id: 0 }} tuneThread={tuneThread} />
                            ))}
                            {testState.judgement && <Judgement judgement={testState.judgement} />}
                        </>
                    )}
                </>
            ) : (
                <Box>Test the personality to see results</Box>
            )}
            {tuneThread.referenceAnswer && (
                <>
                    <Divider
                        color="gray"
                        label={`Reference answer ${refOpened ? "(Hide)" : "(Show 1)"}`}
                        onClick={refToggle}
                        className={classes.clickDivider}
                    />
                    {refOpened && (
                        <MessageItemView
                            item={{
                                type: "MESSAGE",
                                role: "ASSISTANT",
                                content: tuneThread.referenceAnswer,
                                context: false,
                                toolCallId: null,
                                toolName: null,
                                toolArguments: null,
                            }}
                            opened={true}
                            onOpen={() => {}}
                            onClose={() => {}}
                            showActions={false}
                            containerClassName={itemClasses.chatBox}
                        />
                    )}
                </>
            )}
            <Divider color="gray" />
            <Group gap="sm">
                <EditReferenceButton tuneThread={tuneThread} disabled={testState?.status === "LOADING"} />
                <DeleteThreadButton tuneThread={tuneThread} disabled={testState?.status === "LOADING"} />
                <AddItemButton tuneThread={tuneThread} disabled={testState?.status === "LOADING"} />
                <CloneThreadButton
                    tuneThread={tuneThread}
                    disabled={testState?.status === "LOADING"}
                    testItems={testState?.status === "SUCCESS" ? testState.items : []}
                />
                {handleClear && (
                    <ClearButton
                        tuneThread={tuneThread}
                        onClear={(id) => handleClear(id)}
                        disabled={!testState || testState.status === "LOADING"}
                    />
                )}
                {handleTest && aiFormValues && (
                    <Button
                        onClick={() => {
                            handleTest(aiFormValues, tuneThread.id);
                        }}
                        disabled={testState?.status === "LOADING"}
                        type="button"
                    >
                        Test
                    </Button>
                )}
            </Group>
        </Stack>
    );
}

export default TuneThread;
